import isMaterialWarehouse from '@/common/is-warehouse.js'
import { hasDuplicates } from '@/utils/array.js'
import * as utils from '@/views/creative/utils'

/**
 * cookPermissions
 * 加工权限 key，注意前端的 key 不要与后端接口的 key 冲突
 * @param {{page: string, isPreviewBasicPackage: boolean, version: number, roles: string[], isQcEngine: boolean}} param0
 */
const cookPermissions = ({
  page,
  isPreviewBasicPackage,
  roles,
  version,
  isQcEngine,
}) => {
  const isTemplatePage = utils.isTemplatePage(page)
  const isCommonTemplateCustomPage = utils.isCommonTemplateCustomPage(page)
  const isCommonTemplatePage = isTemplatePage && page === 'common-template'

  // language
  const languagePermissionStrategy = [
    {
      // 是否可新增语言
      validator() {
        return isTemplatePage
      },
      key: 'language.add-and-delete',
    },
  ]

  // game config
  const gameConfigPermissionStrategy = [
    {
      // game config 的 slider 组件是否可编辑最大值最小值
      validator() {
        return isTemplatePage
      },
      key: 'game-config.slider-range-editable',
    },
  ]

  // category
  const categoryPermissionStrategy = [
    // 分类迁移
    {
      validator() {
        return isTemplatePage
      },
      key: 'category.move',
    },

    // 扩展内容
    {
      validator() {
        return isMaterialWarehouse()
      },
      key: 'category.expanded-content',
    },

    // 排序
    {
      validator() {
        return isTemplatePage
      },
      key: 'category.sort',
    },

    {
      validator() {
        return isMaterialWarehouse() && isTemplatePage
      },
      key: 'category.update-desc',
    },
  ]

  // dco
  const dcoPermissionStrategy = [
    // 转换 dco
    {
      validator() {
        // 模板编辑页面
        return isTemplatePage && !isCommonTemplatePage && !isPreviewBasicPackage
      },
      key: 'dco.convert-to-dco',
    },
  ]

  const resourceStrategy = [
    // 素材仓库资源上传按钮
    {
      validator() {
        return isMaterialWarehouse()
      },
      key: 'resource.warehouse-upload-button',
    },

    // pt 资源上传按钮
    {
      validator() {
        return !isMaterialWarehouse()
      },
      key: 'resource.pt-upload-button',
    },

    // 只有通用模板制作页面才有个人资产库的使用能力
    // 前期只有 assetMode=true 才有这个能力，后续会去掉
    // 前期只有 image/audio/mp4 才有资产库的使用能力
    // PT 改版套餐上线之后，这里默认都是用资产库资源，如果是通用模版制作都是使用 assets uploader
    {
      validator() {
        return isCommonTemplateCustomPage
      },
      key: 'resource.pt-assets-upload-button',
    },

    // 非通用模板制作页面都走原有的逻辑
    {
      validator() {
        return !isCommonTemplateCustomPage
      },
      key: 'resource.pt-no-assets-upload-button',
    },

    // 基础包资源是否可编辑
    {
      validator() {
        if (isMaterialWarehouse()) {
          // 素材仓库通用模板编辑页基础包资源可编辑
          return isCommonTemplatePage
        }
        // pt 基础包资源可编辑
        return true
      },
      key: 'resource.basic-resource-updatable',
    },

    // 基础包资源是否可删除
    {
      validator() {
        if (isMaterialWarehouse()) return false
        // pt 基础包资源可删除（非真实删除）
        return true
      },
      key: 'resource.basic-resource-deletable',
    },
  ]

  // 导航条
  const navbarPermissionStrategy = [
    {
      // PT 的两个页面需要自定义 navbar
      validator() {
        return !isMaterialWarehouse()
      },
      key: 'navbar',
    },
    {
      validator() {
        return isCommonTemplateCustomPage
      },
      key: 'navbar.copy-creative',
    },
    {
      validator() {
        return !isCommonTemplateCustomPage
      },
      key: 'navbar.more-operate',
    },
  ]

  const i18nPermissionStrategy = [
    {
      validator() {
        return isQcEngine
      },
      // 暂时青瓷引擎制作的素材才支持渐变色
      key: 'i18n.gradient',
    },
  ]

  const permissionStrategy = [
    ...gameConfigPermissionStrategy,
    ...languagePermissionStrategy,
    ...categoryPermissionStrategy,
    ...dcoPermissionStrategy,
    ...resourceStrategy,
    ...navbarPermissionStrategy,
    ...i18nPermissionStrategy,

    // 重置 JSON
    {
      validator() {
        return isTemplatePage
      },
      key: 'template-json-resettable',
    },

    // 重置模板
    {
      validator() {
        return isTemplatePage && !isCommonTemplatePage
      },
      key: 'template-resettable',
    },

    // 右侧版本列表
    {
      validator() {
        if (isMaterialWarehouse()) return false
        // 通用模板制作不需要版本列表
        if (isCommonTemplateCustomPage) {
          return false
        }
        return true
      },
      key: 'version-list',
    },

    // 是否允许云端选取
    {
      validator() {
        return isMaterialWarehouse()
      },
      key: 'allow-cloud-selector',
    },

    // 预览组件的保存按钮
    {
      validator() {
        // PT 的两个页面不需要预览组件的保存按钮
        return isMaterialWarehouse()
      },
      key: 'save-button-in-previewer',
    },

    {
      // 通用模板制作的模板页编辑时允许新增皮肤
      validator() {
        return isMaterialWarehouse() && isCommonTemplatePage
      },
      key: 'template-skin-editable',
    },
  ]

  const fePermissionKeys = []
  const rec = (list) => {
    list.map((item) => {
      if (typeof item.validator === 'function' && item.validator()) {
        fePermissionKeys.push(item.key)
      }
      if (Array.isArray(item.validator)) rec(item.validator)
    })
  }
  rec(permissionStrategy)

  if (hasDuplicates([...roles, ...fePermissionKeys])) {
    throw new Error('有重复权限 key')
  }

  // 合并后端与前端key列表
  return [...roles, ...fePermissionKeys]
}

export default cookPermissions
